import { useEffect, useState } from 'react';
import { trackView } from '../utils/analytics';
import { useCart } from '../utils/cart';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BadgeCheck, Calendar, Check, Clock, Download, Globe, Heart, MapPin, MessageCircle, Route, Share2, Star, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { useCatalog, LANG_KEYS } from '../data/catalog';
import { waConfigured, waLink } from '../config/whatsapp';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { Reveal } from '../components/Reveal';
import { PriceOnRequest } from '../components/PriceOnRequest';
import { ExternalDistributionLink } from '../components/ExternalDistributionLink';
import { RequestAvailabilityForm } from '../components/RequestAvailabilityForm';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { EcoAssistContextButton } from '../components/EcoAssistContextButton';
import { useCuratedCopy } from '../data/curatedArchitecture';
import { FavoriteButton } from '../components/FavoriteButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Meta = ({ icon: Icon, label, value }) =>
  value ? (
    <div className="flex items-start gap-3">
      <Icon size={16} className="mt-0.5 shrink-0 text-eco-gold" />
      <div>
        <p className="eco-price-label">{label}</p>
        <p className="text-sm font-medium text-eco-ink">{value}</p>
      </div>
    </div>
  ) : null;

const List = ({ items, icon: Icon = Check, cls = 'text-eco-green' }) => (
  <ul className="space-y-2">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2.5 text-sm text-eco-ink/70">
        <Icon size={15} className={`mt-0.5 shrink-0 ${cls}`} /> {item}
      </li>
    ))}
  </ul>
);

const Block = ({ title, children, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-eco-ink/10 bg-white p-6 md:p-7">
    <h2 className="text-xl leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink">{title}</h2>
    <div className="eco-body mt-4">{children}</div>
  </div>
);

const DetailGrid = ({ value }) => value && Object.keys(value).length ? (
  <dl className="grid gap-3 sm:grid-cols-2">{Object.entries(value).filter(([, item]) => item !== '' && item !== null && item !== undefined && item !== false && (!Array.isArray(item) || item.length)).map(([key, item]) => <div key={key}><dt className="eco-price-label">{key.replaceAll('_', ' ')}</dt><dd className="mt-1 text-sm text-eco-ink/70">{Array.isArray(item) ? item.join(' · ') : item === true ? 'Sim' : String(item)}</dd></div>)}</dl>
) : null;

export default function ExperienceDetailPage() {
  const { slug } = useParams();
  const { add: addToCart } = useCart();
  const { t, lang } = useLang();
  const curated = useCuratedCopy(lang);
  const { experienceBySlug, experiencesLoaded } = useCatalog();
  const e = experienceBySlug(slug);
  const [reviews, setReviews] = useState([]);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestProduct, setRequestProduct] = useState('');
  const openRequest = (product) => { setRequestProduct(product || ''); setRequestOpen(true); };

  useEffect(() => {
    if (e?.slug) trackView('experience', e.slug);
  }, [e?.slug]);

  useEffect(() => {
    if (!e) return;
    fetch(`${API}/reviews?experience=${encodeURIComponent(e.name)}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setReviews(Array.isArray(d) ? d : []))
      .catch(() => setReviews([]));
  }, [e]);

  const seoDescription = e ? (e.short_summary || e.overview || '').slice(0, 150) : 'Experiência Eco Collection';
  useSEO(e ? `${e.name} | Eco Collection` : 'Experiência | Eco Collection', seoDescription, { image: e?.img, ogTitle: e?.name, ogDescription: seoDescription });

  if (!e) {
    if (!experiencesLoaded) return <div className="min-h-[60vh] bg-eco-cream" data-testid="experience-loading" />;
    return <Navigate to="/experiences" replace />;
  }

  const addToTrip = () => {
    const result = addToCart({ type: 'experience', slug: e.slug, name: e.name, destination: e.island || e.destination });
    if (!result.added) toast(t('cart.duplicate'), { description: e.name });
    else toast.success(t('cta.addtrip'), { description: e.name });
  };

  const share = async () => {
    const url = `${window.location.origin}/experiences/${encodeURIComponent(e.slug)}`;
    try {
      if (navigator.share) await navigator.share({ title: e.name, text: e.short_summary || e.overview || '', url });
      else { await navigator.clipboard.writeText(url); toast.success(t('share.copied')); }
    } catch (error) {
      if (error?.name !== 'AbortError') toast.error(t('share.error'));
    }
  };

  return (
    <div data-testid={`experience-page-${slug}`}>
      <section className="relative overflow-hidden bg-eco-deep pt-28 md:pt-32">
        <img src={e.img} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-eco-deep/60 to-eco-deep" />
        <div className="relative mx-auto max-w-6xl px-5 pb-12 md:px-8">
          <Link to="/experiences" data-testid="experience-back-link" className="inline-flex items-center gap-1.5 text-sm text-white/60 transition-colors hover:text-eco-gold">
            <ArrowLeft size={15} /> {t('back')}
          </Link>
          <Reveal className="mt-6">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-eco-gold">{e.destination} · {e.category}</p>
            <h1 className="eco-page-title mt-3 max-w-3xl text-white">{e.name}</h1>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {e.rating_summary ? <span className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-white/10 px-4 text-sm font-semibold text-white"><Star size={15} fill="currentColor" className="text-eco-gold" /> {e.rating_summary.average} ({e.rating_summary.count} {t('reviews.count')})</span> : <span className="text-sm text-white/55">{t('reviews.none')}</span>}
              <FavoriteButton experience={e} />
              <button type="button" onClick={share} aria-label={t('share.action')} data-testid="experience-share" className="inline-flex min-h-11 items-center gap-2 rounded-full border border-white/25 px-4 text-sm font-semibold text-white hover:border-eco-gold"><Share2 size={16} /> {t('share.action')}</button>
            </div>
            <p className="mt-4 flex flex-wrap items-center gap-4 text-sm leading-[1.5] font-normal text-white/70">
              <span className="flex items-center gap-1.5"><MapPin size={14} className="text-eco-gold" /> {e.location}</span>
              <span className="flex items-center gap-1.5"><Clock size={14} className="text-eco-gold" /> {e.duration}</span>
              {e.distance && <span className="flex items-center gap-1.5"><Route size={14} className="text-eco-gold" /> {e.distance} · {e.level}</span>}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-eco-cream py-12 md:py-16">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 md:px-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="max-w-[720px] space-y-6">
            <Reveal>
              <div className="overflow-hidden rounded-2xl">
                <img src={e.img} alt={e.name} className="h-72 w-full object-cover md:h-96" />
              </div>
            </Reveal>
            {(e.full_description || e.overview || e.short_summary) && <Reveal><Block title={t('detail.overview')} testid="experience-overview"><p className="whitespace-pre-line leading-relaxed text-eco-ink/70">{e.full_description || e.overview || e.short_summary}</p></Block></Reveal>}
            {e.highlights?.length > 0 && <Reveal><Block title={t('detail.highlights')}><List items={e.highlights} /></Block></Reveal>}
            {(e.itinerary?.length > 0 || e.route?.length > 0) && (
              <Reveal>
                <Block title={t('detail.route')} testid="experience-route">
                  <ol className="space-y-2.5">
                    {(e.itinerary?.length ? e.itinerary : e.route).map((r, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm text-eco-ink/70">
                        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-eco-green/10 font-serif text-xs font-bold text-eco-green">{i + 1}</span>
                        <div><strong>{typeof r === 'string' ? r : (r.name || r.title)}</strong>{typeof r === 'object' && <><p>{[r.time, r.travel_time, r.transport_mode, r.duration, r.activity_type].filter(Boolean).join(' · ')}</p>{r.description && <p className="mt-1">{r.description}</p>}</>}</div>
                      </li>
                    ))}
                  </ol>
                </Block>
              </Reveal>
            )}
            {e.walking && Object.keys(e.walking).length > 0 && <Reveal><Block title={t('detail.walkingNature')}><DetailGrid value={e.walking} /></Block></Reveal>}
            {e.transport && Object.keys(e.transport).length > 0 && <Reveal><Block title={t('detail.transport')}><DetailGrid value={e.transport} /></Block></Reveal>}
            {e.food_drinks && Object.keys(e.food_drinks).length > 0 && <Reveal><Block title={t('detail.foodDrinks')}><DetailGrid value={e.food_drinks} /></Block></Reveal>}
            {(e.included?.length > 0 || e.notIncluded?.length > 0) && <Reveal>
              <div className="grid gap-6 sm:grid-cols-2">
                {e.included?.length > 0 && <Block title={t('detail.included')}><List items={e.included} /></Block>}
                {e.notIncluded?.length > 0 && <Block title={t('detail.notIncluded')}><List items={e.notIncluded} icon={X} cls="text-red-400" /></Block>}
              </div>
            </Reveal>}
            {e.bring?.length > 0 && <Reveal><Block title={t('detail.bring')}><List items={e.bring} icon={ArrowRight} cls="text-eco-gold" /></Block></Reveal>}
            {e.pickup && Object.keys(e.pickup).length > 0 && <Reveal><Block title={t('detail.pickup')}><DetailGrid value={e.pickup} /></Block></Reveal>}
            {e.cancellation_weather && Object.keys(e.cancellation_weather).length > 0 && <Reveal><Block title={t('detail.cancellationWeather')}><DetailGrid value={e.cancellation_weather} /></Block></Reveal>}
            {e.safety_public && Object.keys(e.safety_public).length > 0 && <Reveal><Block title={t('detail.safety')}><DetailGrid value={e.safety_public} /></Block></Reveal>}
            {e.guide_public?.information && (
              <Reveal>
                <Block title={t('detail.guide')} testid="experience-guide">
                  <p className="whitespace-pre-line leading-relaxed text-eco-ink/70">{e.guide_public.information}</p>
                  {e.guide_public.languages?.length > 0 && <p className="mt-3 text-xs font-medium uppercase tracking-[0.12em] text-eco-ink/45">{e.guide_public.languages.join(' · ')}</p>}
                </Block>
              </Reveal>
            )}
            {e.extended_expedition?.description && (
              <Reveal>
                <Block title={e.extended_expedition.title || t('exp.expedition.title')} testid="experience-expedition">
                  <p className="whitespace-pre-line leading-relaxed text-eco-ink/70">{e.extended_expedition.description}</p>
                  <button
                    data-testid="experience-expedition-cta"
                    onClick={() => openRequest(`${e.name} — ${e.extended_expedition.title || 'Extended Expedition'}`)}
                    className="eco-btn-primary mt-4"
                  >
                    {e.extended_expedition.cta || t('exp.expedition.cta')} <ArrowRight size={14} />
                  </button>
                </Block>
              </Reveal>
            )}
            {e.gallery?.length > 0 && <Reveal><Block title={t('detail.gallery')}><div className="grid grid-cols-2 gap-3">{e.gallery.map((image) => <figure key={image.url}><img src={image.url} alt={image.alt || e.name} className="aspect-[4/3] w-full rounded-xl object-cover" />{image.caption && <figcaption className="mt-1 text-xs text-eco-ink/55">{image.caption}</figcaption>}</figure>)}</div></Block></Reveal>}
            {e.location_details && Object.keys(e.location_details).length > 0 && <Reveal><Block title={t('detail.locationPublic')}><DetailGrid value={e.location_details} />{e.location_details.maps_url && <a href={e.location_details.maps_url} target="_blank" rel="noopener noreferrer" className="eco-btn-text mt-4">{t('detail.openMap')} <ArrowRight size={14} /></a>}</Block></Reveal>}
            {e.important && <Reveal><Block title={t('detail.info')}><p className="text-sm leading-relaxed text-eco-ink/70">{e.important}</p></Block></Reveal>}
            {reviews.length > 0 && (
              <Reveal>
                <Block title="Reviews reais" testid="experience-reviews">
                  <div className="space-y-4">
                    {reviews.map((r) => (
                      <div key={r.id} data-testid={`experience-review-${r.id}`} className="rounded-2xl bg-eco-cream p-4">
                        <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-eco-ink">{r.traveler_name} · {r.rating}/5 {r.verified_experience && <span className="inline-flex items-center gap-1 rounded-full bg-eco-green/10 px-2 py-1 text-[10px] uppercase tracking-wide text-eco-green"><BadgeCheck size={12} /> {t('reviews.verified')}</span>}</p>
                        {r.text && <p className="mt-1 text-sm leading-relaxed text-eco-ink/60">“{r.text}”</p>}
                      </div>
                    ))}
                  </div>
                </Block>
              </Reveal>
            )}
          </div>

          <div className="space-y-6">
            <Reveal delay={0.1} className="lg:sticky lg:top-24">
              <div className="rounded-2xl border border-eco-ink/10 bg-white p-6 shadow-sm">
                <p className="eco-field-label mb-4">{t('exp.information')}</p>
                <div className="grid grid-cols-2 gap-4">
                  <Meta icon={MapPin} label={t('filter.destination')} value={`${e.island}, ${e.destination}`} />
                  <Meta icon={Clock} label={t('filter.duration')} value={e.duration} />
                  <Meta icon={MapPin} label={t('detail.meeting')} value={e.meetingPoint} />
                  <Meta icon={Calendar} label={t('detail.start')} value={e.startTime} />
                  <Meta icon={Globe} label={t('detail.languages')} value={e.languages?.map((l) => t(LANG_KEYS[l] || l)).join(' · ')} />
                  <Meta icon={Users} label={t('detail.group')} value={e.groupSize} />
                </div>
                <div className="mt-6 border-t border-eco-ink/10 pt-5">
                  <PriceOnRequest
                    testid="experience-price-state"
                    showCta
                    onClick={() => openRequest()}
                  />
                  <button
                    data-testid="experience-add-trip"
                    onClick={addToTrip}
                    className="eco-btn-text mt-2 min-h-11 w-full justify-center"
                  >
                    <Heart size={15} /> {t('cta.addtrip')}
                  </button>
                  <EcoAssistContextButton context="experience" className="mt-2 w-full justify-center">{curated.assist.experience}</EcoAssistContextButton>
                  <ExternalDistributionLink links={e.external_distribution} testid="experience-external-distribution" />
                  {e.brochure_enabled && <a href={`${API}/experiences/${e.slug}/brochure?lang=${lang}`} target="_blank" rel="noopener noreferrer" className="eco-btn-secondary mt-3 w-full justify-center" data-testid="experience-brochure-download"><Download size={15} /> {t('detail.brochure')}</a>}
                </div>
                {e.partner && (
                  <div className="mt-6 border-t border-eco-ink/10 pt-5" data-testid="experience-operator">
                    <p className="eco-field-label">{t('detail.operatedby')}</p>
                    <p className="mt-2.5 flex items-center gap-1.5 text-sm font-semibold text-eco-ink">
                      <BadgeCheck size={15} className="text-eco-green" /> {e.partner}
                    </p>
                    <p className="mt-0.5 text-xs text-eco-ink/50">{t('detail.localop')}</p>
                    <Link to="/partners" data-testid="experience-partner-link" className="eco-btn-text mt-2 text-xs">{t('detail.viewpartner')}</Link>
                  </div>
                )}
                {waConfigured && (
                  <a
                    href={waLink(t('wa.exp', { exp: e.name, dest: e.destination }))}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="experience-whatsapp-cta"
                    className="mt-3 flex min-h-12 items-center justify-center gap-2 rounded-[10px] bg-[#25D366] px-6 text-[15px] font-semibold text-white transition-colors duration-200 hover:bg-[#1eb85a] md:min-h-11"
                  >
                    <MessageCircle size={16} /> {t('cta.whatsapp')}
                  </a>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent
          data-testid="experience-request-dialog"
          className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-[640px] gap-0 overflow-y-auto rounded-2xl border-eco-ink/10 bg-eco-cream p-5 sm:p-7"
        >
          <DialogHeader>
            <DialogTitle className="sr-only">{t('cta.availability')}</DialogTitle>
            <DialogDescription className="sr-only">{e.name}</DialogDescription>
          </DialogHeader>
          <RequestAvailabilityForm
            testid="availability-form"
            kind="availability"
            destination={`${e.island}, ${e.destination}`}
            product={requestProduct || e.name}
            experienceId={e.id}
            experienceSlug={e.slug}
            meetingPoint={e.meetingPoint}
            showAccommodation
            bare
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
